import {
  ADD_B2B_ITEM,
  UPDATE_ORDER_ITEM_STATUS,
} from '@buncha/config/graphQL/documentNode/mutation';
import {
  AddB2BItemParams,
  AddB2BItemResponse,
  B2BCatalogItem,
  GetB2BCatalogItemResponse,
} from '../types/ScannedAddItem';
import {
  getPrefixFromNSC2,
  getPriceFromNSC2,
  isNSC2,
  isPlu,
} from '../utils/ScannedAddItem';
import {useCallback, useRef, useState} from 'react';

import {GET_B2B_CATALOG_ITEM} from '@buncha/config/graphQL/documentNode/queries';
import {OrderItemStatus} from '../types';
import {ToastType} from '@buncha/components/composites/notification/type';
import {gqlService} from '@buncha/config/graphQL';
import {useToastMessage} from '@buncha/components/composites/notification';

export function useCheckItemToAdd() {
  const [loading, setLoading] = useState(false);
  const [addItemCheckError, setAddItemCheckError] = useState('');

  const checkItem = async (
    code: string,
    orderId: number,
    storeIndex: string,
  ) => {
    setLoading(true);
    try {
      const res = await gqlService?.query<GetB2BCatalogItemResponse>({
        query: GET_B2B_CATALOG_ITEM,
        fetchPolicy: 'network-only',
        variables: {
          orderId,
          storeIndex,
          codeArgs: {
            pluCode: code,
          },
        },
      });
      return res;
    } catch (err: any) {
      setAddItemCheckError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const clearError = () => {
    setAddItemCheckError('');
  };

  return {checkItem, addItemCheckError, loading, clearError};
}

export function useUpdateOrderItemStatus() {
  const [changingStatus, setChangingStatus] = useState(false);
  const [showErrorToast] = useToastMessage(ToastType.Error);
  const changeOrderItemStatus = async (
    orderItemIds: number[],
    status: OrderItemStatus,
    runId: number,
  ) => {
    setChangingStatus(true);
    try {
      await gqlService?.mutation({
        mutation: UPDATE_ORDER_ITEM_STATUS,
        variables: {
          runId: runId,
          orderItemIds: orderItemIds,
          orderItemStatus: status,
        },
      });
    } catch (err: any) {
      showErrorToast(err?.message);
    } finally {
      setChangingStatus(false);
    }
  };
  return {changeOrderItemStatus, changingStatus};
}

export function useScannedAddItem(initialPlu: string, orderId?: number) {
  const [showErrorToast] = useToastMessage(ToastType.Error);
  const [loading, setLoading] = useState(false);

  const scannedPlu = useRef<string>('');
  const [addItemLoading, setAddItemLoading] = useState(false);
  const [item, setItem] = useState<B2BCatalogItem>();
  const scannedItemFetch = async (
    storeIndex: string,
    upc?: string,
    plu?: string,
    skipFlow?: boolean,
  ) => {
    if (
      scannedPlu.current &&
      upc &&
      (isPlu(scannedPlu.current) || isNSC2(scannedPlu.current)) &&
      isNSC2(upc)
    )
      return true;
    if (!upc && !plu) return;
    let code;
    let generatedPlu;
    if (upc) {
      code = getPrefixFromNSC2(upc);
      if (code === upc) {
        code = undefined;
        generatedPlu = upc;
      }
    }

    setLoading(true);
    try {
      const res = await gqlService?.query<GetB2BCatalogItemResponse>({
        query: GET_B2B_CATALOG_ITEM,
        fetchPolicy: 'network-only',
        variables: {
          orderId,
          storeIndex,
          codeArgs: {
            nsc2Code: code,
            pluCode: Number(plu ?? generatedPlu).toString(),
          },
        },
      });
      const catalogItem = res?.data?.getB2BCatalogItem;
      if (!catalogItem) return;
      if (
        scannedPlu.current &&
        scannedPlu.current !== catalogItem?.upc &&
        !skipFlow &&
        !isNSC2(catalogItem?.upc)
      )
        return showErrorToast('Item does not match');

      const price = upc && isNSC2(upc) ? getPriceFromNSC2(upc) : undefined;
      if (upc && price)
        setItem({
          ...(catalogItem ?? {}),
          price,
        });
      else setItem(catalogItem);
      scannedPlu.current = catalogItem?.upc;
      return catalogItem;
    } catch (err: any) {
      showErrorToast(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const clearItem = () => setItem(undefined);

  const clearScannedPlu = () => (scannedPlu.current = '');

  const addScannedItem = useCallback(
    async (params: AddB2BItemParams) => {
      setAddItemLoading(true);
      try {
        const res = await gqlService?.mutation<AddB2BItemResponse>({
          mutation: ADD_B2B_ITEM,
          variables: params,
          fetchPolicy: 'network-only',
        });
        return res?.data?.addItemInRun;
      } catch (err: any) {
        showErrorToast(err?.message);
      } finally {
        setAddItemLoading(false);
      }
    },
    [showErrorToast],
  );

  return {
    scannedItemFetch,
    loading,
    item,
    clearItem,
    addScannedItem,
    addItemLoading,
    clearScannedPlu,
  };
}
